import React from 'react';
import { Shield, Check, X, Clock, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import Button from '../components/Button';
import FAQAccordion from '../components/FAQAccordion';
import CTASection from '../components/CTASection';
import { faqsData } from '../data/faqsData';

export default function PatientFamily({ onOpenConsultation }) {
  const admissionSteps = [
    {
      step: "01",
      title: "Initial Confidential Contact",
      desc: "Reach out via our 24/7 helpline or consultation form. Our clinical coordinator discusses current symptoms, urgency, and arranges immediate evaluation."
    },
    {
      step: "02",
      title: "Comprehensive Psychiatric Intake",
      desc: "Upon arrival at our reception suite, the patient undergoes preliminary clinical and medical assessment by our on-duty consultant psychiatrist."
    },
    {
      step: "03",
      title: "Family Counseling & Consent",
      desc: "Transparent discussion with family members detailing recommended treatment duration, safety protocols, accommodation choices, and mutual expectations."
    },
    {
      step: "04",
      title: "Orientation & Room Allocation",
      desc: "The patient is escorted to their private or companion room, introduced to the primary nursing team, and initiated on gentle stabilization care."
    }
  ];

  const permittedItems = [
    "4–5 sets of comfortable, modest seasonal clothing",
    "Slip-on or velcro shoes / comfortable sandals (no long laces)",
    "Personal toiletries in plastic squeeze containers (no glass bottles)",
    "Government-issued photo identification (Aadhar / Passport)",
    "All previous medical records, diagnostic scans, and current prescriptions",
    "Eyeglasses / contact lens care (if applicable)"
  ];

  const prohibitedItems = [
    "Sharp items, scissors, razors, nail cutters, or mirrors",
    "Belts, drawstrings, cords, or wire hangers (anti-ligature policy)",
    "Outside medications, alcohol, tobacco, or unapproved chemical substances",
    "Excessive cash, valuable jewelry, or expensive electronic valuables",
    "Glass bottles, tin cans, or aerosol spray canisters"
  ];

  const dailySchedule = [
    { time: "06:30 AM – 07:30 AM", activity: "Gentle Morning Routine, Vitals Check & Hydration" },
    { time: "07:30 AM – 08:30 AM", activity: "Guided Yoga, Mindfulness Meditation & Deep Breathing" },
    { time: "08:30 AM – 09:30 AM", activity: "Nutritious Dietitian-Curated Breakfast & Morning Medication" },
    { time: "09:30 AM – 11:30 AM", activity: "Doctor Rounds, Individual Psychotherapy & CBT Sessions" },
    { time: "11:30 AM – 01:00 PM", activity: "Occupational Therapy, Cognitive Remediation & Art Studio" },
    { time: "01:00 PM – 02:00 PM", activity: "Nutritious Lunch & Guided Digestive Rest" },
    { time: "02:00 PM – 04:00 PM", activity: "Structured Rest / Psychoeducation Workshop" },
    { time: "04:00 PM – 05:00 PM", activity: "Evening Tea & Group Dynamics / Peer Support Circle" },
    { time: "05:00 PM – 06:30 PM", activity: "Outdoor Garden Walking, Recreation & Family Visiting Hours" },
    { time: "07:30 PM – 08:30 PM", activity: "Wholesome Dinner, Relaxed Reading & Leisure" },
    { time: "09:00 PM – 10:00 PM", activity: "Night Medication, Sleep Hygiene Routine & Restful Lights Out" }
  ];

  return (
    <div className="bg-canvas">
      <SEO
        title="Patient & Family Guide | Admissions, Checklist & Rights"
        description="Comprehensive guide for patients and families: Step-by-step admission process, what to bring, daily therapeutic schedule, patient rights, and visiting hours."
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                Guidance During Stressful Times
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
              Patient & Family <span className="text-[#2A9D8F]">Admission Guide</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#D9E8E5] leading-relaxed">
              We know seeking psychiatric or deaddiction care for a loved one is emotionally overwhelming. Here is clear, step-by-step guidance on what to expect, what to bring, and how we protect your family's dignity.
            </p>
          </div>
        </div>
      </section>

      {/* 1. Admission Process Steps */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {admissionSteps.map((step) => (
              <div
                key={step.step}
                className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card hover:shadow-medium transition-all flex flex-col justify-between"
              >
                <div>
                  <span className="font-serif text-3xl text-[#0F766E] font-bold block mb-4">
                    {step.step}
                  </span>
                  <h3 className="font-serif text-xl text-[#123B5D] font-normal mb-2.5">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Packing Checklist: What to Bring vs Prohibited Items */}
      <section className="py-20 lg:py-28 bg-[#F5FAF9] border-y border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="PREPARATION CHECKLIST"
            title="What to Bring During Inpatient Admission"
            subtitle="To maintain maximum patient safety, our admissions team reviews all items upon arrival."
            align="center"
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Permitted Items */}
            <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card">
              <div className="flex items-center gap-3 mb-6 text-[#123B5D]">
                <div className="p-2 rounded-xl bg-[#F5FAF9] text-[#0F766E]">
                  <Check className="w-5 h-5 text-[#0F766E]" />
                </div>
                <h3 className="font-serif text-2xl font-normal text-[#123B5D]">
                  Permitted & Recommended Items
                </h3>
              </div>

              <ul className="space-y-3.5">
                {permittedItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-xs sm:text-sm text-[#64748B]">
                    <CheckCircle2 className="w-4 h-4 text-[#0F766E] flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prohibited Items */}
            <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card">
              <div className="flex items-center gap-3 mb-6 text-[#123B5D]">
                <div className="p-2 rounded-xl bg-[#F5FAF9] text-amber-700">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <h3 className="font-serif text-2xl font-normal text-[#123B5D]">
                  Restricted & Prohibited Items
                </h3>
              </div>

              <ul className="space-y-3.5">
                {prohibitedItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-xs sm:text-sm text-[#64748B]">
                    <X className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* 3. A Typical Day in Rehabilitation (Schedule) */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="STRUCTURED HEALING"
            title="A Typical Day in Inpatient Rehabilitation"
            subtitle="Predictability and healthy circadian rhythms are fundamental to psychiatric stabilization and neurological recovery."
            align="center"
            className="mb-14"
          />

          <div className="bg-white rounded-3xl border border-[#D9E8E5] shadow-card overflow-hidden divide-y divide-[#D9E8E5]">
            {dailySchedule.map((item, idx) => (
              <div key={idx} className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-[#F5FAF9]/60 transition-colors">
                <div className="flex items-center gap-3 text-xs font-bold text-[#0F766E] sm:w-60 flex-shrink-0">
                  <Clock className="w-4 h-4 text-[#2A9D8F] flex-shrink-0" />
                  <span>{item.time}</span>
                </div>
                <div className="text-xs sm:text-sm text-[#64748B] font-medium">
                  {item.activity}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Patient Rights & Privacy Charter */}
      <section className="py-20 lg:py-28 bg-[#123B5D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <SectionHeading
                badge="DIGNITY & ETHICS"
                title="Patient Rights & Absolute Privacy Guarantee"
                subtitle="We operate with rigorous adherence to mental health ethics, human rights, and protected confidentiality."
                dark={true}
              />

              <div className="space-y-3.5 pt-2">
                {[
                  "Right to respectful, compassionate, and non-discriminatory care regardless of background",
                  "Right to complete privacy: Medical records and patient identity are never disclosed without legal guardian consent",
                  "Right to clear, transparent information regarding diagnosis, treatment plans, and medications",
                  "Right to clean, safe, ligature-free accommodation and nutritious dietitian meals",
                  "Right to regular family communication and guided visiting sessions"
                ].map((right, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-[#D9E8E5]">
                    <Shield className="w-4 h-4 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                    <span>{right}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="p-8 sm:p-10 rounded-3xl bg-[#0D273E]/90 border border-white/10 space-y-6">
                <div className="flex items-center gap-3 text-[#2A9D8F]">
                  <Users className="w-6 h-6 text-[#2A9D8F]" />
                  <h3 className="font-serif text-2xl font-normal text-white">
                    24/7 Operations & Family Counseling
                  </h3>
                </div>

                <div className="space-y-3 text-xs sm:text-sm text-[#D9E8E5]">
                  <p>
                    <strong className="text-white">Hospital Working Hours:</strong> Open 24 Hours / 7 Days a Week (All Days)
                  </p>
                  <p>
                    <strong className="text-white">Emergency Admissions:</strong> 24/7 Immediate Triage Available
                  </p>
                  <p>
                    <strong className="text-white">Family Counseling & Therapy:</strong> Daily appointments scheduled by treating psychologist / social worker.
                  </p>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <Button
                    onClick={onOpenConsultation}
                    variant="primary"
                    size="md"
                    className="w-full"
                  >
                    Speak With an Admissions Counselor
                  </Button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. Comprehensive FAQs */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="ALL QUESTIONS ANSWERED"
            title="Frequently Asked Questions for Families"
            subtitle="Clear, verified answers to help you make informed decisions for your loved one."
            align="center"
            className="mb-14"
          />

          <div className="space-y-12">
            {faqsData.map((cat, idx) => (
              <div key={idx} className="space-y-4">
                <h3 className="font-serif text-2xl text-[#123B5D] font-normal border-b border-[#D9E8E5] pb-3">
                  {cat.category}
                </h3>
                <FAQAccordion items={cat.questions} defaultOpenIndex={idx === 0 ? 0 : -1} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
